import { Map as ImmutableMap } from 'immutable';
import { isRelationalReport, isSingleObjectReport } from 'reporting-data/tsTypes/reportTypes';
import ConditionalFormattingColumnSelect from './ConditionalFormattingColumnSelect';
import { useState } from 'react';
import RemoveConditionalFormattingButton from './RemoveConditionalFormattingButton';
import { createRelationalReportFromBaseReport } from 'reporting-snowflake/relational/utils/report-utils';
import ConditionalFormattingMinAndMax from './ConditionalFormattingMinAndMax';
import useFetchDependenciesForSnowflakeUpdaters from '../../hooks/useFetchDependenciesForSnowflakeUpdaters';
import ConditionalFormattingColorPalette from './ConditionalFormattingColorPalette';
import { Section } from 'foundations-components/transitional/components/Section';
import ConditionalFormattingReapply from './ConditionalFormattingReapply';
import { EVENTS } from '../../lib/events';
import useCloneTracker from '../../hooks/useCloneTracker';
import { getDimensions, getMetrics } from 'reporting-data/report/configReportGetters';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getInitialFormattedColumns = report => {
  if (isRelationalReport(report)) {
    var _snowflakeReport$visu, _encodings$get$filter;
    const snowflakeReport = createRelationalReportFromBaseReport(report);
    const encodings = (_snowflakeReport$visu = snowflakeReport.visual) === null || _snowflakeReport$visu === void 0 ? void 0 : _snowflakeReport$visu.encodings;
    return (_encodings$get$filter = encodings.get('columns').filter(encoding => encoding && encoding.getIn(['options', 'format', 'conditionalFormatting', 'useConditionalFormatting']))) === null || _encodings$get$filter === void 0 ? void 0 : _encodings$get$filter.map(encoding => encoding.get('column')).toArray();
  }
  if (isSingleObjectReport(report)) {
    const metrics = getMetrics(report).map(metric => metric.get('property'));
    const dimensions = getDimensions(report);
    const columns = metrics.concat(dimensions);
    const formattedColumns = [];
    if (!columns) {
      return [];
    }
    columns.forEach(column => {
      const conditionalFormatting = report.getIn(['displayParams', 'columns', column, 'format', 'conditionalFormatting'], ImmutableMap());
      if (conditionalFormatting && conditionalFormatting.get('useConditionalFormatting')) {
        formattedColumns.push(column);
      }
    });
    return formattedColumns;
  }
  return [];
};
const ConditionalFormattingSettings = ({
  report,
  onChange,
  appUsageTracker
}) => {
  const tracker = useCloneTracker(appUsageTracker);
  const initialFormattedColumns = getInitialFormattedColumns(report);
  const reportDeps = useFetchDependenciesForSnowflakeUpdaters(report);
  const [selectedColumnName, setSelectedColumnName] = useState(initialFormattedColumns ? initialFormattedColumns[0] : undefined);
  const [formattedColumns, setFormattedColumns] = useState(initialFormattedColumns);
  const [selectedColumnWasJustDeleted, setSelectedColumnWasJustDeleted] = useState(false);
  const onChangeColumn = columnName => {
    setSelectedColumnName(columnName);
    setSelectedColumnWasJustDeleted(false);
    if (formattedColumns.length === 0) {
      tracker === null || tracker === void 0 || tracker.track(EVENTS.conditionalFormattingInteraction, {
        actionType: 'activate'
      });
    }
    setFormattedColumns(prevColumns => {
      if (prevColumns.indexOf(columnName) < 0) {
        return prevColumns.concat(columnName);
      }
      return prevColumns;
    });
  };
  const onRemoveColumn = columnName => {
    tracker === null || tracker === void 0 || tracker.track(EVENTS.conditionalFormattingInteraction, {
      actionType: 'removeColumn'
    });
    setSelectedColumnWasJustDeleted(true);
    setFormattedColumns(prevColumns => prevColumns.filter(column => column !== columnName));
  };
  const onReapplyToColumn = () => {
    if (!selectedColumnName) {
      return;
    }
    tracker === null || tracker === void 0 || tracker.track(EVENTS.conditionalFormattingInteraction, {
      actionType: 'reapplyToColumn'
    });
    setSelectedColumnWasJustDeleted(false);
    setFormattedColumns(prevColumns => prevColumns.concat(selectedColumnName));
  };
  const shouldRenderConditionalFormattingControls = selectedColumnName && !selectedColumnWasJustDeleted && formattedColumns.indexOf(selectedColumnName) >= 0;
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(Section, {
      children: /*#__PURE__*/_jsx(ConditionalFormattingColumnSelect, {
        report: report,
        reportDeps: reportDeps,
        onChange: onChange,
        selectedColumnName: selectedColumnName,
        onChangeColumn: onChangeColumn,
        tracker: tracker
      })
    }), shouldRenderConditionalFormattingControls && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Section, {
        children: /*#__PURE__*/_jsx(ConditionalFormattingColorPalette, {
          report: report,
          onChange: onChange,
          selectedColumnName: selectedColumnName,
          tracker: tracker
        })
      }), /*#__PURE__*/_jsx(Section, {
        children: /*#__PURE__*/_jsx(ConditionalFormattingMinAndMax, {
          report: report,
          reportDeps: reportDeps,
          onChange: onChange,
          selectedColumnName: selectedColumnName,
          tracker: tracker
        })
      }), /*#__PURE__*/_jsx(Section, {
        children: /*#__PURE__*/_jsx(RemoveConditionalFormattingButton, {
          report: report,
          reportDeps: reportDeps,
          onClick: onRemoveColumn,
          selectedColumnName: selectedColumnName,
          onChange: onChange
        })
      })]
    }), selectedColumnWasJustDeleted && selectedColumnName && /*#__PURE__*/_jsx(ConditionalFormattingReapply, {
      report: report,
      selectedColumnName: selectedColumnName,
      onReapplyToColumn: onReapplyToColumn,
      onChange: onChange
    })]
  });
};
export default ConditionalFormattingSettings;